import type { ApolloCache } from '@apollo/client';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import Quill from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import type { SetNonNullable } from 'type-fest';
import { useDebouncedCallback } from 'use-debounce';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import ConfirmationModal from '../components/ConfirmationModal';
import type { MagneticWallFocus } from '../editor/editorSlice';
import { CREATION_ID, EditorActions } from '../editor/editorSlice';
import AnnotationAttributor from '../editor/formats/Annotation';
import Comment from '../editor/formats/Comment';
import Reaction from '../editor/formats/Reaction';
import {
  getAttributorType,
  getClosestAttribute,
} from '../editor/formats/utils/attributors';
import { useQuill } from '../editor/QuillContext';
import { getHeaderCollapse } from '../editor/useHeaderCollapse';
import generateDeltaRandomId from '../editor/utils/generateDeltaRandomId';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type {
  CommentThread as CommentThreadType,
  Comment as CommentType,
  Post,
  PublicPost,
  ReactionThread as ReactionThreadType,
} from '../graphql/generated/graphql';
import { CreateUpdateDeleteAction } from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';
import { useBeforeLeaveConfirm } from '../utils/BeforeLeaveConfirm';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';
import addFragmentReference from '../utils/graphql/addFragmentReference';
import removeReferenceById from '../utils/graphql/removeReferenceById';
import type {
  ExtractCommentThreadTypes,
  ExtractReactionThreadTypes,
} from '../utils/post/threadUtils';
import {
  isCommentOrPublicCommentThread,
  isReactionOrPublicReactionThread,
} from '../utils/post/threadUtils';
import useRemoveHash from '../utils/router/useRemoveHash';
import commentThreadHash from '../utils/url/commentThreadHash';
import reactionThreadHash from '../utils/url/reactionThreadHash';
import useIsPublicView from '../utils/useIsPublicView';
import Annotation from './annotation/Annotation';
import AnnotationSheet from './annotation/AnnotationSheet';
import { OPTIMISTIC_COMMENT_IDS } from './comment/CommentCreate';
import { COMMENT_REACTION_LIST_REACTIONS_FRAGMENT } from './comment/CommentReactionList';
import CommentThread from './comment/CommentThread';
import CommentThreadSheet from './comment/CommentThreadSheet';
import getDeltaForRemovingThread from './comment/getDeltaForRemovingThread';
import type { CompactPopOverSide } from './comment/getMagneticWallItemLayout';
import { VerticalSpan } from './comment/getMagneticWallItemLayout';
import ReactionThread from './comment/ReactionThread';
import ReactionThreadSheet from './comment/ReactionThreadSheet';
import showFullPageSheet from './comment/showFullPageSheet';
import { OPTIMISTIC_REACTION_IDS } from './comment/ThreadReactionList';
import getCurrentMarkForComment from './comment/utils/getCurrentMarkForComment';
import HoverContentPreview from './HoverContentPreview';
import * as styles from './MagneticWall.module.scss';
import type { MagneticWallItemSize } from './MagneticWallItem';
import type { RelativeContentRects } from './MagneticWallLayout';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from './MagneticWallLayout';
import { usePostLayoutState } from './PostLayoutProvider';
import useMagneticWallItemSize from './useMagneticWallItemSize';
import { useOptimisticItemContentRects } from './useOptimisticItemContentRects';
import useVisibleMagneticWallItems from './useVisibleMagneticWallItems';
import getCollapsedHeaderRect from './utils/getCollapsedHeaderRect';
import getContentRectForCurrentSelection from './utils/getContentRectForCurrentSelection';
import parseMagneticWallHash from './utils/parseMagneticWallHash';
import toRelativeDOMRect from './utils/toRelativeDOMRect';
import useUnresolveThread from './utils/useUnresolveThread';

const MAGNETIC_WALL_MAX_WIDTH = parseInt(styles.magneticWallMaxWidth, 10);

const POST_FRAGMENT = gql(`
  fragment MagneticWall_post on RootPostFields {
    id
    ...CommentThread_post
    ...ReactionThread_post
    ...CommentThreadSheet_post
    ...Annotation_post
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments, slab-graphql/no-unused-fields
const REACTION_FIELDS_FRAGMENT = gql(`
  fragment ReactionFields on ReactionFields {
    id
    emoji
    author {
      id
      name
      ...UserAvatar
      ...getUserNames_user
    }
    insertedAt
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
gql(`
  fragment CommentReaction on CommentReaction {
    id
    emoji
    author {
      id
      name
      ...getUserNames_user
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
const COMMENT_FIELDS_FRAGMENT = gql(`
  fragment CommentFields on CommentFields {
    id
    content
    author {
      id
      name
      ...UserAvatar
    }
    insertedAt
    updatedAt
    reactions {
      ...CommentReaction
    }
  }
`);

const MAGNETIC_WALL_THREADS_FRAGMENT = gql(`
  fragment MagneticWall_threads on RootThreadFields {
    id
    ...CommentThread_thread
    ...ReactionThread_thread
    ...CommentThreadSheet_thread
    ...ReactionThreadSheet_thread
  }
`);

const MAGNETIC_WALL_ANNOTATIONS_FRAGMENT = gql(`
  fragment MagneticWall_annotations on Annotation {
    id
    ...Annotation_annotation
  }
`);

const THREAD_SET_SUBSCRIPTION = gql(/** @complexity 264 */ `
  subscription threadSet($postId: ID!) {
    postThreadSet(postId: $postId) {
      action
      thread {
        id
        resolvedAt
        ...MagneticWall_threads
      }
      comment {
        id
        thread {
          id
        }
        ...CommentFields
      }
      annotation {
        id
        ...MagneticWall_annotations
      }
      reaction {
        id
        thread {
          id
        }
        ...ReactionFields
      }
      commentReaction {
        id
        commentId
        threadId
        ...CommentReaction
      }
    }
  }
`);

const addThreadToPost = (
  cache: ApolloCache<unknown>,
  thread: DocumentType<typeof MAGNETIC_WALL_THREADS_FRAGMENT>,
  post: DocumentType<typeof POST_FRAGMENT>,
) => {
  cache.modify<Post>({
    id: cache.identify(post),
    fields: {
      threads: addFragmentReference(
        cache,
        thread,
        MAGNETIC_WALL_THREADS_FRAGMENT,
        'MagneticWall_threads',
      ),
    },
  });
};

const CREATE_COMMENT_MUTATION = gql(/** @complexity 2 */ `
  mutation createComment(
    $threadId: ID!,
    $id: ID!
    $version: Int!
    $checksum: Int!
    $mark: Json!
    $content: String!
    $notifyGroups: Boolean = false
  ) {
    createComment(
      threadId: $threadId
      postId: $id
      version: $version
      checksum: $checksum
      mark: $mark
      content: $content
      notifyGroups: $notifyGroups
    ) {
      id
    }
  }
`);

const useCreateComment = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const [createCommentMutation] = useMutation(CREATE_COMMENT_MUTATION);

  const optimisticItemContentRects = useOptimisticItemContentRects();
  const { quill } = useQuill();
  const sync = useAppSelector((state) => state.editor.sync);
  const dispatch = useAppDispatch();

  return useCallback(
    (commentContent: string, mark: string, notifyGroups: boolean) => {
      if (!sync) throw new Error('sync not inited');

      const threadId = generateDeltaRandomId();

      const contentRectForCurrentSelection = quill
        ? getContentRectForCurrentSelection(quill)
        : null;

      if (contentRectForCurrentSelection) {
        optimisticItemContentRects.add(
          threadId,
          contentRectForCurrentSelection,
        );
      }

      const thread: DocumentType<typeof MAGNETIC_WALL_THREADS_FRAGMENT> =
        (() => {
          const baseComment = {
            content: JSON.parse(commentContent),
            id: generateDeltaRandomId(),
            insertedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            reactions: [],
          };

          if (post.__typename === 'PublicPost') {
            return {
              id: threadId,
              __typename: 'PublicCommentThread' as const,
              author: {
                ...currentUser,
                __typename: 'PublicUser' as const,
              },
              comments: [
                {
                  ...baseComment,
                  author: {
                    ...currentUser,
                    __typename: 'PublicUser' as const,
                  },
                  __typename: 'PublicComment' as const,
                },
              ],
            };
          }

          return {
            id: threadId,
            __typename: 'CommentThread' as const,
            author: {
              ...currentUser,
              __typename: 'User' as const,
            },
            comments: [
              {
                ...baseComment,
                author: {
                  ...currentUser,
                  __typename: 'User' as const,
                },
                __typename: 'Comment' as const,
              },
            ],
          };
        })();

      createCommentMutation({
        optimisticResponse: {
          __typename: 'RootMutationType',
          createComment: {
            id: thread.id,
            __typename: thread.__typename,
          },
        },
        variables: {
          threadId,
          content: commentContent,
          mark,
          id: post.id,
          version: sync.version,
          checksum: sync.checksum,
          notifyGroups,
        },
        update: (cache, result) => {
          if (!result.data) return;
          addThreadToPost(cache, thread, post);
        },
      }).finally(() => {
        optimisticItemContentRects.remove(threadId);
      });

      dispatch(
        EditorActions.focusMagneticWall({
          id: threadId,
          type: 'comment',
          isActive: true,
          hasExpandIntent: false,
        }),
      );
    },
    [
      createCommentMutation,
      post,
      quill,
      optimisticItemContentRects,
      sync,
      dispatch,
      currentUser,
    ],
  );
};

const removeThreadFromPost = (
  cache: ApolloCache<unknown>,
  id: string,
  post: DocumentType<typeof POST_FRAGMENT>,
) => {
  cache.modify({
    id: cache.identify(post),
    fields: {
      threads: removeReferenceById(id),
    },
  });
};

const DELETE_THREAD_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteThread($id: ID!) {
    deleteThread(threadId: $id) {
      id
    }
  }
`);

const useDeleteThread = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const dispatch = useAppDispatch();
  const removeHash = useRemoveHash();
  const [deleteThreadMutation] = useMutation(DELETE_THREAD_MUTATION);

  return useCallback(
    (id: string) => {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Delete entire thread?',
          message: 'All comments in the thread will be deleted.',
          confirmButtonText: 'Delete',
          onConfirm: async () => {
            if (window.location.hash === commentThreadHash(id)) {
              removeHash();
            }
            return deleteThreadMutation({
              variables: { id },
              update: (cache) => removeThreadFromPost(cache, id, post),
            });
          },
        }),
      );
    },
    [dispatch, removeHash, deleteThreadMutation, post],
  );
};

const useDeleteReactionThread = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const dispatch = useAppDispatch();
  const [deleteReactionThread] = useMutation(DELETE_THREAD_MUTATION);
  const removeHash = useRemoveHash();

  return useCallback(
    (id: string) => {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Delete reactions?',
          message: 'All reactions will be deleted.',
          confirmButtonText: 'Delete',
          onConfirm: async () => {
            if (window.location.hash === reactionThreadHash(id)) {
              removeHash();
            }
            await deleteReactionThread({
              variables: { id },
              update: (cache) => removeThreadFromPost(cache, id, post),
            });
          },
        }),
      );
    },
    [dispatch, deleteReactionThread, removeHash, post],
  );
};

const addAnnotationToPost = (
  cache: ApolloCache<unknown>,
  annotation: DocumentType<typeof MAGNETIC_WALL_ANNOTATIONS_FRAGMENT>,
  post: DocumentType<typeof POST_FRAGMENT>,
) => {
  cache.modify<Post>({
    id: cache.identify(post),
    fields: {
      annotations: addFragmentReference(
        cache,
        annotation,
        MAGNETIC_WALL_ANNOTATIONS_FRAGMENT,
        'MagneticWall_annotations',
      ),
    },
  });
};

const CREATE_ANNOTATION = gql(/** @complexity 2 */ `
  mutation createAnnotation(
    $id: ID!
    $checksum: Int!
    $content: Json!
    $link: String
    $mark: Json!
    $postId: ID!
    $version: Int!
    $linkText: String
  ) {
    createAnnotation(
      id: $id
      checksum: $checksum
      content: $content
      mark: $mark
      link: $link
      postId: $postId
      version: $version
      linkText: $linkText
    ) {
      id
    }
  }
`);

const useCreateAnnotation = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const { quill } = useQuill();

  const dispatch = useAppDispatch();
  const [createAnnotation] = useMutation(CREATE_ANNOTATION);
  const sync = useAppSelector((state) => state.editor.sync);
  const optimisticItemContentRects = useOptimisticItemContentRects();

  return useCallback(
    ({
      content,
      sanitizedLink,
      linkText,
    }: {
      content: Op[];
      sanitizedLink: string | null;
      linkText: string;
    }) => {
      if (!sync) throw new Error('sync not inited');
      if (!quill) throw new Error('quill not inited');

      const optimisticAnnotation = {
        __typename: 'Annotation' as const,
        content,
        id: generateDeltaRandomId(),
        link: sanitizedLink,
        linkText,
      };

      const contentRectForCurrentSelection =
        getContentRectForCurrentSelection(quill);

      if (contentRectForCurrentSelection) {
        optimisticItemContentRects.add(
          optimisticAnnotation.id,
          contentRectForCurrentSelection,
        );
      }

      createAnnotation({
        variables: {
          id: optimisticAnnotation.id,
          content: JSON.stringify(content),
          mark: JSON.stringify(getCurrentMarkForComment(quill)),
          postId: post.id,
          link: sanitizedLink,
          linkText,
          version: sync.version,
          checksum: sync.checksum,
        },
        optimisticResponse: {
          __typename: 'RootMutationType',
          createAnnotation: {
            id: optimisticAnnotation.id,
            __typename: optimisticAnnotation.__typename,
          },
        },
        update: (cache, result) => {
          if (!result.data) return;
          addAnnotationToPost(cache, optimisticAnnotation, post);
        },
      }).finally(() => {
        optimisticItemContentRects.remove(optimisticAnnotation.id);
      });

      dispatch(
        EditorActions.focusMagneticWall({
          id: optimisticAnnotation.id,
          type: 'annotation',
          isActive: true,
          hasExpandIntent: false,
        }),
      );
    },
    [post, quill, sync, createAnnotation, optimisticItemContentRects, dispatch],
  );
};

const removeAnnotationFromPost = (
  cache: ApolloCache<unknown>,
  id: string,
  post: DocumentType<typeof POST_FRAGMENT>,
) => {
  cache.modify({
    id: cache.identify(post),
    fields: {
      annotations: removeReferenceById(id),
    },
  });
};

const DELETE_ANNOTATION = gql(/** @complexity 2 */ `
  mutation deleteAnnotation($id: ID!) {
    deleteAnnotation(id: $id) {
      id
    }
  }
`);

const useDeleteAnnotation = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const dispatch = useAppDispatch();

  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION);

  return useCallback(
    (id: string) => {
      return new Promise<void>((resolve) => {
        dispatch(
          UIActions.openModal(ConfirmationModal, {
            title: 'Delete Annotation?',
            message: 'This will delete the annotation.',
            confirmButtonText: 'Delete',
            onConfirm: async () => {
              deleteAnnotation({
                variables: { id },
                optimisticResponse: { deleteAnnotation: { id } },
                update: (cache) => removeAnnotationFromPost(cache, id, post),
              });
              resolve();
              return Promise.resolve();
            },
          }),
        );
      });
    },
    [deleteAnnotation, dispatch, post],
  );
};

const removeCommentFromThread = (
  cache: ApolloCache<unknown>,
  thread: NonNullable<
    NonNullable<
      DocumentType<typeof THREAD_SET_SUBSCRIPTION>['postThreadSet']
    >['comment']
  >['thread'],
  commentId: string,
) => {
  cache.modify({
    id: cache.identify(thread),
    fields: {
      comments: removeReferenceById(commentId),
    },
  });
};

const DELETE_COMMENT_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteComment($id: ID!) {
    deleteComment(commentId: $id) {
      id
    }
  }
`);

const useDeleteComment = () => {
  const [deleteComment] = useMutation(DELETE_COMMENT_MUTATION, {});

  return useCallback(
    (
      thread: ExtractCommentThreadTypes<typeof MAGNETIC_WALL_THREADS_FRAGMENT>,
      commentId: string,
    ) => {
      deleteComment({
        variables: { id: commentId },
        optimisticResponse: {
          __typename: 'RootMutationType' as const,
          deleteComment: {
            id: commentId,
            __typename:
              thread.__typename === 'PublicCommentThread'
                ? ('PublicComment' as const)
                : ('Comment' as const),
          },
        },
        update: (cache) => removeCommentFromThread(cache, thread, commentId),
      });
    },
    [deleteComment],
  );
};

const addCommentToThread = (
  cache: ApolloCache<unknown>,
  comment: DocumentType<typeof COMMENT_FIELDS_FRAGMENT>,
  thread: NonNullable<
    NonNullable<
      DocumentType<typeof THREAD_SET_SUBSCRIPTION>['postThreadSet']
    >['comment']
  >['thread'],
) => {
  cache.modify<CommentThreadType>({
    id: cache.identify(thread),
    fields: {
      comments: addFragmentReference(
        cache,
        comment,
        COMMENT_FIELDS_FRAGMENT,
        'CommentFields',
      ),
    },
  });
};

const ADD_COMMENT_MUTATION = gql(/** @complexity 14 */ `
  mutation addComment(
    $id: ID!
    $content: String!
    $notifyGroups: Boolean = false
  ) {
    addComment(threadId: $id, content: $content, notifyGroups: $notifyGroups) {
      ...CommentFields
    }
  }
`);

const getBaseComment = (content: string) => {
  return {
    content: JSON.parse(content),
    id: generateDeltaRandomId(),
    insertedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    reactions: [],
  };
};

const useAddComment = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isPublicView = useIsPublicView();

  const [addComment] = useMutation(ADD_COMMENT_MUTATION, {});

  return useCallback(
    (
      thread: ExtractCommentThreadTypes<typeof MAGNETIC_WALL_THREADS_FRAGMENT>,
      variables: VariablesOf<typeof ADD_COMMENT_MUTATION>,
    ) => {
      const baseComment = getBaseComment(variables.content);
      OPTIMISTIC_COMMENT_IDS.add(baseComment.id);

      addComment({
        variables,
        optimisticResponse: () => {
          if (isPublicView) {
            return {
              __typename: 'RootMutationType' as const,
              addComment: {
                ...baseComment,
                author: {
                  ...currentUser,
                  __typename: 'PublicUser' as const,
                },
                __typename: 'PublicComment' as const,
              },
            };
          }

          return {
            __typename: 'RootMutationType' as const,
            addComment: {
              ...baseComment,
              author: {
                ...currentUser,
                __typename: 'User' as const,
              },
              __typename: 'Comment' as const,
            },
          };
        },
        update: (cache, { data }) => {
          if (!data) return;
          addCommentToThread(cache, data.addComment, thread);
        },
      }).finally(() => {
        OPTIMISTIC_COMMENT_IDS.delete(baseComment.id);
      });
    },
    [addComment, isPublicView, currentUser],
  );
};

const addReactionToThread = (
  cache: ApolloCache<unknown>,
  reaction: DocumentType<typeof REACTION_FIELDS_FRAGMENT>,
  thread: NonNullable<
    NonNullable<
      DocumentType<typeof THREAD_SET_SUBSCRIPTION>['postThreadSet']
    >['reaction']
  >['thread'],
) => {
  cache.modify<ReactionThreadType>({
    id: cache.identify(thread),
    fields: {
      reactions: (refs, { readField, toReference }) => {
        const newReactionKey = `${reaction.emoji}-${reaction.author.id}`;

        const keysWithRef = refs.map((ref) => {
          const emoiji = readField('emoji', ref) as string;
          const authorId = readField('id', readField('author', ref)) as string;
          const key = `${emoiji}-${authorId}`;

          if (key === newReactionKey) {
            // this makes sure that the reaction will be stored with the real id from the response
            return {
              ref: toReference(reaction, true),
              key,
            };
          }

          return {
            ref,
            key,
          };
        });

        const hasReaction = keysWithRef.find(
          ({ key }) => key === newReactionKey,
        );

        if (!hasReaction) {
          return [
            ...keysWithRef.map(({ ref }) => ref),
            toReference(reaction, true),
          ];
        }

        return refs;
      },
    },
  });
};

const ADD_REACTION_MUTATION = gql(/** @complexity 2 */ `
  mutation addReaction($emoji: String!, $threadId: ID!) {
    addReaction(emoji: $emoji, threadId: $threadId) {
      id
      insertedAt
    }
  }
`);

export const useAddReaction = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const [addReaction] = useMutation(ADD_REACTION_MUTATION);

  return (
    thread: ExtractReactionThreadTypes<typeof MAGNETIC_WALL_THREADS_FRAGMENT>,
    emoji: string,
  ) => {
    const baseReaction = {
      emoji,
      id: generateDeltaRandomId(),
      insertedAt: new Date().toISOString(),
    };

    const optimisticReaction: DocumentType<typeof REACTION_FIELDS_FRAGMENT> =
      thread.__typename === 'PublicReactionThread'
        ? {
            ...baseReaction,
            author: { ...currentUser, __typename: 'PublicUser' as const },
            __typename: 'PublicReaction' as const,
          }
        : {
            ...baseReaction,
            author: currentUser,
            __typename: 'Reaction' as const,
          };

    OPTIMISTIC_REACTION_IDS.add(optimisticReaction.id);

    addReaction({
      variables: { emoji, threadId: thread.id },
      optimisticResponse: {
        __typename: 'RootMutationType',
        addReaction: optimisticReaction,
      },
      update: (cache, { data }) => {
        if (!data) return;
        addReactionToThread(
          cache,
          {
            ...optimisticReaction,
            // we want to use the real id from the response when resolves,
            // so that we can update it in the cache correctly.
            id: data.addReaction.id,
          },
          thread,
        );
      },
    }).then(() => {
      OPTIMISTIC_REACTION_IDS.delete(optimisticReaction.id);
    });
  };
};

const removeReactionFromThread = (
  cache: ApolloCache<unknown>,
  thread: NonNullable<
    NonNullable<
      DocumentType<typeof THREAD_SET_SUBSCRIPTION>['postThreadSet']
    >['reaction']
  >['thread'],
  reactionId: string,
) => {
  cache.modify({
    id: cache.identify(thread),
    fields: {
      reactions: removeReferenceById(reactionId),
    },
  });
};

const DELETE_REACTION_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteReaction($id: ID!) {
    deleteReaction(reactionId: $id) {
      id
    }
  }
`);

const useDeleteReaction = () => {
  const { quill } = useQuill();
  const [deleteReaction] = useMutation(DELETE_REACTION_MUTATION);

  const removeReactionThreadFromEditor = (threadId: string) => {
    if (!quill) return;
    const range = quill.getSelection();
    const change = getDeltaForRemovingThread(quill.getContents(), threadId);
    if (change.length() === 0) {
      return;
    }
    quill.editReadOnly(() => {
      // prevent undo from re-introducing reaction
      quill.history.cutoff();

      quill.history.ignoreChange = true;
      quill.updateContents(change, Quill.sources.USER);
      quill.history.ignoreChange = false;
    });
    if (range) {
      quill.root.focus({ preventScroll: true });
      quill.setSelection(range, Quill.sources.SILENT);
    }
  };

  return (
    thread: ExtractReactionThreadTypes<typeof MAGNETIC_WALL_THREADS_FRAGMENT>,
    id: string,
  ) => {
    // if the reaction is optimistic, this is a no-op
    if (OPTIMISTIC_REACTION_IDS.has(id)) return;

    if (thread.reactions.length === 1) {
      // only 1 reaction, delete the thread too
      removeReactionThreadFromEditor(thread.id);
    }
    deleteReaction({
      variables: { id },
      optimisticResponse: {
        __typename: 'RootMutationType',
        deleteReaction: {
          id,
          __typename:
            thread.__typename === 'PublicReactionThread'
              ? 'PublicReaction'
              : 'Reaction',
        },
      },
      update: (cache, { data }) => {
        if (!data) return;
        removeReactionFromThread(cache, thread, data.deleteReaction.id);
      },
    });
  };
};

const addReactionToComment = (
  cache: ApolloCache<unknown>,
  reaction: DocumentType<typeof COMMENT_REACTION_LIST_REACTIONS_FRAGMENT>,
  comment: Pick<
    DocumentType<typeof COMMENT_FIELDS_FRAGMENT>,
    '__typename' | 'id'
  >,
) => {
  cache.modify<CommentType>({
    id: cache.identify(comment),
    fields: {
      reactions: addFragmentReference(
        cache,
        reaction,
        COMMENT_REACTION_LIST_REACTIONS_FRAGMENT,
        'CommentReactionList_reactions',
      ),
    },
  });
};

const removeReactionFromComment = (
  cache: ApolloCache<unknown>,
  reactionId: string,
  comment: Pick<
    DocumentType<typeof COMMENT_FIELDS_FRAGMENT>,
    '__typename' | 'id'
  >,
) => {
  cache.modify<CommentType>({
    id: cache.identify(comment),
    fields: {
      reactions: removeReferenceById(reactionId),
    },
  });
};

const CREATE_COMMENT_REACTION_MUTATION = gql(/** @complexity 2 */ `
  mutation createCommentReaction($commentId: ID!, $emoji: String!) {
    commentReaction: createCommentReaction(
      commentId: $commentId
      emoji: $emoji
    ) {
      id
    }
  }
`);

const DELETE_COMMENT_REACTION_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteCommentReaction($id: ID!) {
    commentReaction: deleteCommentReaction(id: $id) {
      id
    }
  }
`);

const useToggleCommentReaction = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);

  const [createCommentReactionMutation] = useMutation(
    CREATE_COMMENT_REACTION_MUTATION,
  );
  const [deleteCommentReactionMutation] = useMutation(
    DELETE_COMMENT_REACTION_MUTATION,
  );

  const createCommentReaction = (
    comment: DocumentType<typeof COMMENT_FIELDS_FRAGMENT>,
    emoji: string,
  ) => {
    const optimisticReaction: DocumentType<
      typeof COMMENT_REACTION_LIST_REACTIONS_FRAGMENT
    > = {
      emoji,
      id: generateDeltaRandomId(),
      __typename: 'CommentReaction',
      author:
        comment.__typename === 'PublicComment'
          ? { ...currentUser, __typename: 'PublicUser' }
          : currentUser,
    };

    createCommentReactionMutation({
      variables: { commentId: comment.id, emoji },
      optimisticResponse: {
        __typename: 'RootMutationType',
        commentReaction: optimisticReaction,
      },
      update: (cache, { data }) => {
        if (!data) return;
        addReactionToComment(
          cache,
          {
            ...optimisticReaction,
            id: data.commentReaction.id,
          },
          comment,
        );
      },
    });
  };

  const deleteCommentReaction = (
    comment: DocumentType<typeof COMMENT_FIELDS_FRAGMENT>,
    id: string,
  ) =>
    deleteCommentReactionMutation({
      variables: { id },
      optimisticResponse: {
        __typename: 'RootMutationType',
        commentReaction: { __typename: 'CommentReaction', id },
      },
      update: (cache) => removeReactionFromComment(cache, id, comment),
    });

  const toggleCommentReaction = (
    thread: DocumentType<typeof MAGNETIC_WALL_THREADS_FRAGMENT>,
    comment: DocumentType<typeof COMMENT_FIELDS_FRAGMENT>,
    emoji: string,
  ) => {
    const reaction = comment.reactions.find(
      (item) => item.emoji === emoji && item.author.id === currentUser.id,
    );

    dispatch(
      EditorActions.focusMagneticWall({
        id: thread.id,
        type: 'comment',
        isActive: true,
        hasExpandIntent: true,
      }),
    );

    if (reaction) {
      deleteCommentReaction(comment, reaction.id);
    } else {
      dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
      createCommentReaction(comment, emoji);
    }
  };

  return toggleCommentReaction;
};

export const POST_THREADS_QUERY = gql(/** @complexity 103 */ `
  query postCommentThreads($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        ...MagneticWall_threads
      }
      annotations {
        ...MagneticWall_annotations
      }
    }
  }
`);

const PUBLIC_POST_THREADS_QUERY = gql(/** @complexity 103 */ `
  query publicPostCommentThreads($postId: ID!) {
    post: publicPost(id: $postId) {
      id
      threads {
        id
        ...MagneticWall_threads
      }
      annotations {
        ...MagneticWall_annotations
      }
    }
  }
`);

const useMagneticWallItems = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const postId = post.id;
  const isPublicView = useIsPublicView();
  const optimisticItemContentRects = useOptimisticItemContentRects();

  const { data } = useQuery<
    | DocumentType<typeof PUBLIC_POST_THREADS_QUERY>
    | DocumentType<typeof POST_THREADS_QUERY>
  >(isPublicView ? PUBLIC_POST_THREADS_QUERY : POST_THREADS_QUERY, {
    variables: { postId },
  });

  const { threads, annotations } = useMemo(
    () => ({
      threads: data?.post.threads ?? [],
      annotations: data?.post.annotations ?? [],
    }),
    [data],
  );

  useSubscription(THREAD_SET_SUBSCRIPTION, {
    variables: { postId },
    onData: ({ client, data: subscriptionData }) => {
      const postThreadSet = subscriptionData.data?.postThreadSet;
      if (!postThreadSet) return;
      const { action, annotation, comment, commentReaction, reaction, thread } =
        postThreadSet;
      if (annotation) {
        if (action === CreateUpdateDeleteAction.Create) {
          addAnnotationToPost(client.cache, annotation, post);
          optimisticItemContentRects.remove(annotation.id);
        } else if (action === CreateUpdateDeleteAction.Delete) {
          removeAnnotationFromPost(client.cache, annotation.id, post);
        }
      } else if (thread) {
        if (
          action === CreateUpdateDeleteAction.Create ||
          (action === CreateUpdateDeleteAction.Update && !thread.resolvedAt)
        ) {
          addThreadToPost(client.cache, thread, post);
          optimisticItemContentRects.remove(thread.id);
        } else if (
          action === CreateUpdateDeleteAction.Delete ||
          (action === CreateUpdateDeleteAction.Update && thread.resolvedAt)
        ) {
          removeThreadFromPost(client.cache, thread.id, post);
        }
      } else if (comment) {
        if (action === CreateUpdateDeleteAction.Create) {
          addCommentToThread(client.cache, comment, comment.thread);
        } else if (action === CreateUpdateDeleteAction.Delete) {
          removeCommentFromThread(client.cache, comment.thread, comment.id);
        }
      } else if (commentReaction) {
        const parentThread = threads.find(
          ({ id }) => id === commentReaction.threadId,
        );
        const parentComment =
          parentThread && 'comments' in parentThread
            ? parentThread.comments.find(
                ({ id }) => id === commentReaction.commentId,
              )
            : null;
        if (parentComment) {
          if (action === CreateUpdateDeleteAction.Create) {
            addReactionToComment(client.cache, commentReaction, parentComment);
          } else if (action === CreateUpdateDeleteAction.Delete) {
            removeReactionFromComment(
              client.cache,
              commentReaction.id,
              parentComment,
            );
          }
        }
      } else if (reaction) {
        if (action === CreateUpdateDeleteAction.Create) {
          addReactionToThread(client.cache, reaction, reaction.thread);
        } else if (action === CreateUpdateDeleteAction.Delete) {
          removeReactionFromThread(client.cache, reaction.thread, reaction.id);
        }
      }
    },
  });

  return { threads, annotations };
};

const CREATE_REACTION_MUTATION = gql(/** @complexity 2 */ `
  mutation createReaction(
    $threadId: ID!
    $postId: ID!
    $version: Int!
    $checksum: Int!
    $mark: Json!
    $emoji: String!
  ) {
    createReaction(
      threadId: $threadId
      postId: $postId
      version: $version
      checksum: $checksum
      mark: $mark
      emoji: $emoji
    ) {
      id
    }
  }
`);

export const useCreateReaction = () => {
  const { quill } = useQuill();
  const isPublicView = useIsPublicView();
  const [createReaction] = useMutation(CREATE_REACTION_MUTATION);
  const currentUser = useAppSelector((state) => state.session.user);
  const optimisticItemContentRects = useOptimisticItemContentRects();
  const dispatch = useAppDispatch();

  return (
    postId: string,
    version: number,
    checksum: number,
    mark: string,
    emoji: string,
  ) => {
    const threadId = generateDeltaRandomId();

    const contentRectForCurrentSelection = quill
      ? getContentRectForCurrentSelection(quill)
      : null;

    if (contentRectForCurrentSelection) {
      optimisticItemContentRects.add(threadId, contentRectForCurrentSelection);
    }

    const thread = ((): DocumentType<typeof MAGNETIC_WALL_THREADS_FRAGMENT> => {
      const baseThread = {
        id: threadId,
        comments: [],
      };

      const baseReaction = {
        emoji,
        id: generateDeltaRandomId(),
        insertedAt: new Date().toISOString(),
      };

      if (isPublicView) {
        const publicUser = {
          ...currentUser,
          __typename: 'PublicUser' as const,
        };

        return {
          ...baseThread,
          author: publicUser,
          __typename: 'PublicReactionThread' as const,
          reactions: [
            {
              ...baseReaction,
              author: publicUser,
              __typename: 'PublicReaction' as const,
            },
          ],
        };
      }

      return {
        ...baseThread,
        author: currentUser,
        __typename: 'ReactionThread' as const,
        reactions: [
          {
            ...baseReaction,
            __typename: 'Reaction' as const,
            author: currentUser,
          },
        ],
      };
    })();

    createReaction({
      variables: { threadId, postId, version, checksum, mark, emoji },
      optimisticResponse: {
        __typename: 'RootMutationType',
        createReaction: thread,
      },
      update(cache, { data }) {
        if (!data) return;

        if (isPublicView) {
          cache.modify<PublicPost>({
            id: cache.identify({
              __typename: 'PublicPost',
              id: postId,
            }),
            fields: {
              threads: addFragmentReference(
                cache,
                thread,
                MAGNETIC_WALL_THREADS_FRAGMENT,
                'MagneticWall_threads',
              ),
            },
          });
        } else {
          cache.modify<Post>({
            id: cache.identify({
              __typename: 'Post',
              id: postId,
            }),
            fields: {
              threads: addFragmentReference(
                cache,
                thread,
                MAGNETIC_WALL_THREADS_FRAGMENT,
                'MagneticWall_threads',
              ),
            },
          });
        }
      },
    }).finally(() => {
      optimisticItemContentRects.remove(threadId);
    });

    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'reaction',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
  };
};

type MagneticWallDisplayItemWithPosition = MagneticWallDisplayItem & {
  top: number;
  isOverlapped: boolean;
};

interface BaseMagneticWallProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  magneticWallFocus: MagneticWallFocus | null;
  onResize: (avoidRepositioningFocusedThread?: boolean) => void;
  visibleMagneticWallDisplayItems: MagneticWallDisplayItemWithPosition[];
  compactPopOverSide: CompactPopOverSide;
  focusedDisplayItem: MagneticWallDisplayItem | null;
  canEditPost: boolean;
  magneticWallItemSize: MagneticWallItemSize;
  onPopOverHeightChange: (id: string, height: number) => void;
}

const BaseMagneticWall = ({
  post,
  magneticWallFocus,
  onResize,
  visibleMagneticWallDisplayItems,
  compactPopOverSide,
  focusedDisplayItem,
  canEditPost,
  magneticWallItemSize,
  onPopOverHeightChange,
}: BaseMagneticWallProps) => {
  const { canMagneticWallExpand } = usePostLayoutState();
  const dispatch = useAppDispatch();

  const { quill } = useQuill();

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const [unsavedInputValue, setUnsavedInputValue] = useState<
    Record<string, Op[]>
  >({});

  const debouncedHandleInputChange = useDebouncedCallback(
    (id: string, value: Op[]) =>
      setUnsavedInputValue((unsaved) => ({ ...unsaved, [id]: value })),
    500,
  );

  const isShowingFullSheetItem = showFullPageSheet();

  const unresolveThread = useUnresolveThread();
  const handleUnresolveThread = async (threadId: string) =>
    unresolveThread({ id: threadId });

  const createComment = useCreateComment(post);
  const createAnnotation = useCreateAnnotation(post);
  const deleteAnnotation = useDeleteAnnotation(post);
  const deleteThread = useDeleteThread(post);
  const deleteReactionThread = useDeleteReactionThread(post);
  const deleteComment = useDeleteComment();
  const addComment = useAddComment();
  const addReaction = useAddReaction();
  const deleteReaction = useDeleteReaction();
  const toggleCommentReaction = useToggleCommentReaction();

  const shouldShowFullPageSheet = showFullPageSheet();

  const sortedMagneticWallDisplayItems = _.sortBy(
    visibleMagneticWallDisplayItems,
    (item) => -item.top,
  );

  return (
    <>
      <ClickOutsideHandler
        onClickOutside={(e) => {
          const { target } = e;

          if (
            isShowingFullSheetItem ||
            // The editor is handling the blurring by itself
            (target instanceof Element && quill?.root.contains(target))
          ) {
            return;
          }

          handleBlur();
        }}
      >
        <HoverContentPreview>
          {sortedMagneticWallDisplayItems.map((item) => {
            const { id, type, top } = item;

            const isFocused =
              magneticWallFocus?.id === id &&
              (magneticWallFocus.type === type ||
                (focusedDisplayItem == null &&
                  magneticWallFocus.type === 'comment'));

            const hasExpandIntent =
              isFocused &&
              magneticWallFocus.hasExpandIntent &&
              canMagneticWallExpand;

            const isPopOverOpen =
              isFocused &&
              Boolean(magneticWallFocus.active) &&
              !hasExpandIntent;

            const unsavedInputValueForItem = unsavedInputValue[id];

            return (
              <MagneticWallLayout.Item
                key={id}
                id={id}
                focused={!shouldShowFullPageSheet && isFocused}
                top={top}
                className={styles.item}
              >
                {item.type === 'annotation' && (
                  <Annotation
                    size={magneticWallItemSize}
                    annotation={item}
                    post={post}
                    unsavedInputValue={unsavedInputValueForItem}
                    active={isFocused}
                    popOverOpen={isPopOverOpen}
                    overlapped={item.isOverlapped}
                    compactPopOverSide={compactPopOverSide}
                    onResize={onResize}
                    onInputChange={(ops) => {
                      debouncedHandleInputChange(id, ops);
                      onResize(true);
                    }}
                    canEdit={canEditPost}
                    createAnnotation={createAnnotation}
                    deleteAnnotation={() => deleteAnnotation(item.id)}
                    onPopOverHeightChange={onPopOverHeightChange.bind(null, id)}
                  />
                )}
                {item.type === 'reaction' && (
                  <ReactionThread
                    size={magneticWallItemSize}
                    thread={item}
                    isFocused={isFocused}
                    isPopOverOpen={isPopOverOpen}
                    onResize={onResize}
                    post={post}
                    overlapped={item.isOverlapped}
                    compactPopOverSide={compactPopOverSide}
                    unresolveThread={handleUnresolveThread}
                    deleteReactionThread={() => deleteReactionThread(item.id)}
                    addReaction={(emoji) => {
                      addReaction(item, emoji);
                    }}
                    deleteReaction={(reactionId) => {
                      deleteReaction(item, reactionId);
                    }}
                    onPopOverHeightChange={onPopOverHeightChange.bind(null, id)}
                  />
                )}
                {item.type === 'comment' && (
                  <CommentThread
                    size={magneticWallItemSize}
                    thread={item}
                    post={post}
                    unsavedInputValue={unsavedInputValueForItem}
                    isFocused={isFocused}
                    isPopOverOpen={isPopOverOpen}
                    overlapped={item.isOverlapped}
                    compactPopOverSide={compactPopOverSide}
                    onResize={onResize}
                    onInputChange={(ops) => {
                      debouncedHandleInputChange(id, ops);
                    }}
                    unresolveThread={handleUnresolveThread}
                    createComment={createComment}
                    deleteThread={() => deleteThread(item.id)}
                    deleteComment={(commentId) => {
                      deleteComment(item, commentId);
                    }}
                    addComment={(args) => {
                      addComment(item, args);
                    }}
                    toggleCommentReaction={(comment, emoji) => {
                      toggleCommentReaction(item, comment, emoji);
                    }}
                    onPopOverHeightChange={onPopOverHeightChange.bind(null, id)}
                  />
                )}
              </MagneticWallLayout.Item>
            );
          })}
        </HoverContentPreview>
      </ClickOutsideHandler>

      {isShowingFullSheetItem &&
        focusedDisplayItem &&
        magneticWallFocus?.active && (
          <>
            {focusedDisplayItem.type === 'comment' && (
              <CommentThreadSheet
                post={post}
                thread={focusedDisplayItem}
                onClose={handleBlur}
                onInputChange={(ops) => {
                  debouncedHandleInputChange(focusedDisplayItem.id, ops);
                }}
                unsavedInputValue={unsavedInputValue[focusedDisplayItem.id]}
                createComment={createComment}
                deleteThread={() => deleteThread(focusedDisplayItem.id)}
                deleteComment={(commentId) => {
                  deleteComment(focusedDisplayItem, commentId);
                }}
                addComment={(args) => {
                  addComment(focusedDisplayItem, args);
                }}
                toggleCommentReaction={(comment, emoji) => {
                  toggleCommentReaction(focusedDisplayItem, comment, emoji);
                }}
              />
            )}
            {focusedDisplayItem.type === 'reaction' && (
              <ReactionThreadSheet
                thread={focusedDisplayItem}
                onClose={handleBlur}
                deleteReactionThread={() =>
                  deleteReactionThread(focusedDisplayItem.id)
                }
                addReaction={(emoji) => {
                  addReaction(focusedDisplayItem, emoji);
                }}
                deleteReaction={(id) => {
                  deleteReaction(focusedDisplayItem, id);
                }}
              />
            )}
            {focusedDisplayItem.type === 'annotation' && (
              <AnnotationSheet
                annotation={focusedDisplayItem}
                onClose={handleBlur}
                onInputChange={(ops) =>
                  debouncedHandleInputChange(focusedDisplayItem.id, ops)
                }
                canEdit={canEditPost}
                unsavedInputValue={
                  unsavedInputValue[focusedDisplayItem.id] ?? []
                }
                createAnnotation={createAnnotation}
                deleteAnnotation={() => deleteAnnotation(focusedDisplayItem.id)}
              />
            )}
          </>
        )}
    </>
  );
};

type ElementWithIds = {
  element: HTMLElement;
  ids: string[];
};

function queryElementsWithPrefixedIds({
  container,
  mapIds,
  prefix,
}: {
  container: HTMLElement;
  mapIds(element: HTMLElement): string[];
  prefix: string;
}): ElementWithIds[] {
  const selector = `[class^="${prefix}"], [class*=" ${prefix}"]`;

  return Array.from(container.querySelectorAll<HTMLElement>(selector))
    .map((element) => ({ element, ids: mapIds(element) }))
    .filter(({ ids }) => ids.length);
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const getAbsoluteContentRect = (
  element: HTMLElement,
): DOMRect | null => {
  // For the client rects, we focus on the following properties:
  // 1. top: Determines the position of the thread. This is the most common case.
  // 2. bottom: When the bottom of the content is scrolled out of view, the thread will be shifted up if it pushes the next thread down.
  // 3. left of the first line: Determines the order of threads within the same line.
  const [firstRect, ...restRects] = element.getClientRects();
  if (!firstRect) return null;

  const lastRect = restRects.at(-1);
  if (lastRect && lastRect.bottom > firstRect.bottom) {
    firstRect.height = lastRect.bottom - firstRect.top;
  }

  return DOMRect.fromRect({
    x: firstRect.x,
    y: firstRect.y,
    width: firstRect.width,
    height: firstRect.height,
  });
};

export function getMagneticWallItemContentRects(
  quill: Quill,
): RelativeContentRects[] {
  const { container } = quill;
  const comments = queryElementsWithPrefixedIds({
    container,
    mapIds: (element) => Comment.value(element) ?? [],
    prefix: `${Comment.keyName}-`,
  });

  const reactions = queryElementsWithPrefixedIds({
    container,
    mapIds: (element) => Reaction.value(element) ?? [],
    prefix: `${Reaction.keyName}-`,
  });

  const annotations = queryElementsWithPrefixedIds({
    container,
    mapIds: (element) => AnnotationAttributor.value(element) ?? [],
    prefix: `${AnnotationAttributor.keyName}-`,
  });

  const containerRect = quill.container.getBoundingClientRect();

  const collapsedHeaders =
    getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];

  return [...comments, ...annotations, ...reactions].flatMap(
    ({ element, ids }) => {
      const contentRect =
        getCollapsedHeaderRect(element, collapsedHeaders) ??
        getAbsoluteContentRect(element);
      return contentRect
        ? ids.map((id) => ({
            id,
            rect: toRelativeDOMRect(contentRect, containerRect),
          }))
        : [];
    },
  );
}

type MagneticWallDisplayItem =
  | ({ type: 'annotation' } & DocumentType<
      typeof MAGNETIC_WALL_ANNOTATIONS_FRAGMENT
    >)
  | ({ type: 'reaction' } & ExtractReactionThreadTypes<
      typeof MAGNETIC_WALL_THREADS_FRAGMENT
    >)
  | ({ type: 'comment' } & ExtractCommentThreadTypes<
      typeof MAGNETIC_WALL_THREADS_FRAGMENT
    >);

const useSelectionChangeHandler = (handler: (range: Range) => void) => {
  const lastRangeRef = useRef<Range | null>(null);
  const handlerRef = useRef<(range: Range) => void>(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = () => {
      const selection = document.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      const lastRange = lastRangeRef.current;
      if (
        lastRange &&
        lastRange.startContainer === range.startContainer &&
        lastRange.startOffset === range.startOffset &&
        lastRange.endContainer === range.endContainer &&
        lastRange.endOffset === range.endOffset
      ) {
        return;
      }
      lastRangeRef.current = range;
      handlerRef.current(range);
    };
    document.addEventListener('selectionchange', listener);
    return () => {
      document.removeEventListener('selectionchange', listener);
    };
  }, []);
};

function useFocusThreadAnchor({ skip }: { skip: boolean }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { hash } = location;

  useEffect(() => {
    if (skip) return;

    const parseResult = parseMagneticWallHash(hash);
    if (!parseResult) return;

    dispatch(
      EditorActions.focusMagneticWall({
        id: parseResult.id,
        type: parseResult.type,
        isActive: true,
        hasExpandIntent: parseResult.type === 'comment',
      }),
    );
  }, [dispatch, skip, hash]);
}

interface InnerMagneticWallProps {
  canEditPost: boolean;
  quill: Quill;
  post: DocumentType<typeof POST_FRAGMENT>;
  magneticWallItems: MagneticWallDisplayItem[];
  magneticWallItemSize: MagneticWallItemSize;
}

const InnerMagneticWall = ({
  canEditPost,
  quill,
  post,
  magneticWallItems,
  magneticWallItemSize,
}: InnerMagneticWallProps) => {
  const dispatch = useAppDispatch();
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const {
    debouncedUpdateDesiredPositions,
    updateThreadHeights,
    handlePopOverRender,
    layout,
  } = useMagneticWallLayout();

  const visibleMagneticWallDisplayItems = useMemo(
    () =>
      magneticWallItems
        .map((item) => ({
          ...item,
          top: layout.tops[item.id],
          isOverlapped: layout.overlappedIds.has(item.id),
        }))
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [magneticWallItems, layout.tops, layout.overlappedIds],
  );

  const focusedDisplayItem =
    magneticWallFocus &&
    magneticWallItems.find(
      (item) =>
        item.id === magneticWallFocus.id &&
        item.type === magneticWallFocus.type,
    );

  const isShowingFullSheetItem =
    magneticWallFocus?.active &&
    focusedDisplayItem != null &&
    showFullPageSheet();

  useEffect(() => {
    if (!isShowingFullSheetItem) {
      debouncedUpdateDesiredPositions();
    }
  }, [
    debouncedUpdateDesiredPositions,
    isShowingFullSheetItem,
    magneticWallFocus,
  ]);

  const visibleIds = new Set(
    visibleMagneticWallDisplayItems.map((item) => item.id),
  );

  useVisibleMagneticWallItems(visibleMagneticWallDisplayItems);

  useSelectionChangeHandler((range) => {
    let node = getLowestCommonAncestorContainer(range);
    if (!node) return;

    if (!quill.root.contains(node)) {
      // If the selection is inside the embed selection container.
      const normalized = quill.selection.normalizeNative(range) as {
        start: { node: Node };
      } | null;
      if (normalized) {
        // The end node should always be the same as the start node.
        node = normalized.start.node;
      } else {
        // Ignore when the selection is not inside the editor.
        return;
      }
    }
    const element = node instanceof Element ? node : node.parentElement;

    const closestAttribute = element && getClosestAttribute(element);

    if (!closestAttribute || quill.root.contains(closestAttribute.node)) {
      if (
        closestAttribute?.id &&
        magneticWallFocus?.id !== closestAttribute.id
      ) {
        const type = getAttributorType(closestAttribute.attributor);
        const isVisible = visibleIds.has(closestAttribute.id);
        if (!isVisible) return;

        dispatch(
          EditorActions.focusMagneticWall({
            id: closestAttribute.id,
            type,
            isActive: false,
            hasExpandIntent: false,
          }),
        );
      } else if (closestAttribute?.id == null && magneticWallFocus != null) {
        dispatch(EditorActions.blurMagneticWall());
      }
    }
  });

  useFocusThreadAnchor({
    skip: visibleIds.size === 0,
  });

  return (
    <BaseMagneticWall
      post={post}
      magneticWallFocus={magneticWallFocus}
      onResize={updateThreadHeights}
      visibleMagneticWallDisplayItems={visibleMagneticWallDisplayItems}
      compactPopOverSide={layout.compactPopOverSide}
      focusedDisplayItem={focusedDisplayItem ?? null}
      canEditPost={canEditPost}
      magneticWallItemSize={magneticWallItemSize}
      onPopOverHeightChange={handlePopOverRender}
    />
  );
};

interface MagneticWallProps {
  quill: Quill;
  post: DocumentType<typeof POST_FRAGMENT>;
  canEditPost: boolean;
  innerBannerRef: React.RefObject<HTMLElement>;
  hasBanner: boolean;
}

const MagneticWall = ({
  quill,
  post,
  canEditPost,
  innerBannerRef,
  hasBanner,
}: MagneticWallProps) => {
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const currentUser = useAppSelector((state) => state.session.user);

  const { threads, annotations } = useMagneticWallItems(post);

  const magneticWallDisplayItems: MagneticWallDisplayItem[] = useMemo(() => {
    const isCreating = magneticWallFocus?.id === CREATION_ID;
    const reactionThreads = threads.filter(isReactionOrPublicReactionThread);
    const commentThreads = threads.filter(isCommentOrPublicCommentThread);

    function getNewMagneticWallDisplayItem(): MagneticWallDisplayItem | null {
      const type = magneticWallFocus?.type;
      if (!isCreating || !type) {
        return null;
      }
      switch (type) {
        case 'annotation':
          return {
            __typename: 'Annotation' as const,
            id: CREATION_ID,
            content: [],
            type,
          };
        case 'comment':
          return {
            __typename: 'CommentThread' as const,
            id: CREATION_ID,
            author: currentUser,
            comments: [],
            type,
          };
        case 'reaction':
          return {
            __typename: 'ReactionThread' as const,
            id: CREATION_ID,
            author: currentUser,
            reactions: [],
            type,
          };
        default:
          exhaustiveMatchGuard(type);
          return null;
      }
    }

    return [
      ...annotations.map((data) => ({
        type: 'annotation' as const,
        ...data,
      })),
      ...commentThreads.map((data) => ({
        type: 'comment' as const,
        ...data,
      })),
      ...reactionThreads.map((data) => ({
        type: 'reaction' as const,
        ...data,
      })),
      ..._.compact([getNewMagneticWallDisplayItem()]),
    ];
  }, [annotations, currentUser, magneticWallFocus, threads]);

  const optimisticItemContentRects = useOptimisticItemContentRects();
  useBeforeLeaveConfirm({ skip: optimisticItemContentRects.size === 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const magneticWallItemSize = useMagneticWallItemSize(containerRef);

  return (
    <div className={styles.layoutContainer}>
      <MagneticWallLayout.Root
        containerRef={containerRef}
        itemSize={magneticWallItemSize}
        hasBanner={hasBanner}
        focus={magneticWallFocus}
        magneticWallItems={magneticWallDisplayItems}
        getRelativeContentRects={() => {
          const newContentRects = [
            ...getMagneticWallItemContentRects(quill),
            ...optimisticItemContentRects
              .entries()
              .map(([id, rect]) => ({ id, rect })),
          ];

          if (magneticWallFocus?.id === CREATION_ID) {
            const contentRectForCurrentSelection =
              getContentRectForCurrentSelection(quill);

            if (contentRectForCurrentSelection) {
              return [
                {
                  id: magneticWallFocus.id,
                  rect: contentRectForCurrentSelection,
                },
                ...newContentRects,
              ];
            }
          }

          return newContentRects;
        }}
        getRestrictedSpans={(magneticWallRect) => {
          const containerRect = quill.container.getBoundingClientRect();

          const widerLineRects = Array.from(
            quill.root.querySelectorAll(':scope > .span-full'),
          ).flatMap((node) => {
            if (!(node as HTMLElement).offsetParent) return [];

            const rect = node.getBoundingClientRect();
            return [
              {
                absolute: rect,
                relative: toRelativeDOMRect(rect, containerRect),
              },
            ];
          });

          const magneticWallAbsoluteLeft = magneticWallRect
            ? magneticWallRect.right -
              Math.min(MAGNETIC_WALL_MAX_WIDTH, magneticWallRect?.width)
            : null;

          const intersectingWiderLineSpans = widerLineRects
            .filter(
              ({ absolute }) =>
                !magneticWallAbsoluteLeft ||
                absolute.right > magneticWallAbsoluteLeft,
            )
            .map(({ relative }) => VerticalSpan.fromDOMRect(relative));

          const bannerRect = innerBannerRef.current?.getBoundingClientRect();
          const isBannerIntersecting =
            bannerRect &&
            innerBannerRef.current &&
            magneticWallAbsoluteLeft &&
            bannerRect.right > magneticWallAbsoluteLeft;

          return isBannerIntersecting
            ? [
                VerticalSpan.fromDOMRect(
                  toRelativeDOMRect(
                    innerBannerRef.current.getBoundingClientRect(),
                    containerRect,
                  ),
                ),
                ...intersectingWiderLineSpans,
              ]
            : intersectingWiderLineSpans;
        }}
      >
        <InnerMagneticWall
          canEditPost={canEditPost}
          quill={quill}
          post={post}
          magneticWallItems={magneticWallDisplayItems}
          magneticWallItemSize={magneticWallItemSize}
        />
      </MagneticWallLayout.Root>
    </div>
  );
};

// https://bugzilla.mozilla.org/show_bug.cgi?id=208427
function isFirefoxRestrictedNode(node: Node) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    node.parentNode;
  } catch (err) {
    return true;
  }

  return false;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export function getLowestCommonAncestorContainer(
  originalRange: Range,
): Node | null {
  if (
    isFirefoxRestrictedNode(originalRange.startContainer) ||
    isFirefoxRestrictedNode(originalRange.endContainer)
  ) {
    return null;
  }

  // To get the lowest common ancestor of the current range,
  // we narrow the range while keeping it visually identical.
  if (
    originalRange.collapsed ||
    originalRange.startContainer === originalRange.endContainer
  ) {
    // The range is already the narrowest one.
    return originalRange.commonAncestorContainer;
  }

  const range = originalRange.cloneRange();

  let sc: Node | null = range.startContainer;
  if (
    sc.nodeType === Node.TEXT_NODE &&
    range.startOffset === sc.nodeValue?.length
  ) {
    while (sc && !sc.nextSibling) {
      sc = sc.parentNode;
    }
    if (sc?.nextSibling) {
      range.setStart(sc.nextSibling, 0);
    }
  }

  if (range.endOffset === 0) {
    let ec: Node | null = range.endContainer;
    while (ec && !ec.previousSibling) {
      ec = ec.parentNode;
    }
    if (ec?.previousSibling) {
      const offset =
        ec.previousSibling.nodeType === Node.TEXT_NODE
          ? ec.previousSibling.nodeValue?.length
          : ec.previousSibling.childNodes?.length;
      if (typeof offset === 'number') {
        range.setEnd(ec.previousSibling, offset);
      }
    }
  }

  return range.commonAncestorContainer;
}

export default MagneticWall;
